/**
 * StockReturnPrompt — devolve ao estoque o que foi baixado, quando o orçamento
 * é CANCELADO ou REJEITADO.
 *
 * Espelho do `StockDeductionPrompt`, com três diferenças que importam:
 *
 *  1. **A quantidade vem dos MOVIMENTOS, não do documento.** O usuário pode ter
 *     baixado 2 de um item de 3 (o prompt de baixa deixa editar). Devolver a
 *     quantidade do documento criaria estoque que nunca saiu — e ninguém veria
 *     erro, só um saldo maior do que a prateleira.
 *  2. **O guard é o próprio saldo de movimentos:** se a soma de `quote_sale` +
 *     `sale_return` para esta origem já é zero, não há o que devolver. Isso
 *     torna a devolução idempotente sem depender de marcador local.
 *  3. **Nasce DESLIGADO** (`DEFAULT_STOCK_SETTINGS`): é comportamento novo, e
 *     ninguém pode acordar com o estoque se mexendo sozinho.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Undo2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { supabase } from '@/integrations/supabase/client';
import { useRegisterStockMovement } from '@/hooks/useStock';
import { useStockSettings } from '@/hooks/useStockSettings';
import { netDeductedByProduct, type StockMovementLike } from '@/lib/stockReturn';
import { plural } from '@/lib/plural';

interface Props {
  quoteId: string;
  /** Status atual do orçamento — o gatilho é cancelado/rejeitado. */
  status: string | null | undefined;
}

const dismissKey = (id: string) => `orcamigo_stock_returned_quote_${id}`;

export function StockReturnPrompt({ quoteId, status }: Props) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const inventory = useFeature('inventory');
  const { canWrite } = useRole();
  const register = useRegisterStockMovement();
  const queryClient = useQueryClient();
  const { actionFor } = useStockSettings();
  const autoRan = useRef(false);

  const isCancelled = status === 'cancelled' || status === 'rejected';
  const isRealSource = Boolean(quoteId) && !String(quoteId).startsWith('offline_');
  const action = actionFor('quote_cancelled_return');

  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(dismissKey(quoteId)) === '1';
    } catch {
      return false;
    }
  });

  const { data: pending } = useQuery({
    queryKey: ['stock-return', quoteId, activeCompany?.id],
    enabled:
      Boolean(user?.id && activeCompany?.id) &&
      inventory.allowed &&
      isCancelled &&
      isRealSource &&
      action !== 'off' &&
      !dismissed,
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stock_movements')
        .select('product_id, delta, reason')
        .eq('company_id', activeCompany!.id)
        .eq('quote_id', quoteId)
        .in('reason', ['quote_sale', 'sale_return']);
      if (error) throw error;
      return netDeductedByProduct((data ?? []) as StockMovementLike[]);
    },
  });

  const lines = pending ?? [];

  const runReturn = async (opts?: { auto?: boolean }) => {
    if (lines.length === 0) return;
    try {
      for (const line of lines) {
        await register.mutateAsync({
          productId: line.product_id,
          delta: line.quantity, // positivo: volta para o estoque
          reason: 'sale_return',
          quoteId,
          serviceOrderId: null,
        });
      }
      try {
        localStorage.setItem(dismissKey(quoteId), '1');
      } catch {
        // ignore
      }
      queryClient.invalidateQueries({ queryKey: ['stock-return', quoteId, activeCompany?.id] });
      // Diz que foi automático: estoque que se mexe em silêncio parece bug.
      toast.success(
        opts?.auto
          ? `Devolução automática ao estoque registrada (${plural(lines.length, 'produto', 'produtos')}).`
          : 'Devolução ao estoque registrada.',
      );
      setDismissed(true);
    } catch {
      // erro individual já exibido pela mutation
    }
  };

  const canAct =
    inventory.allowed && canWrite && isCancelled && isRealSource && lines.length > 0 && !dismissed;

  useEffect(() => {
    if (action !== 'auto' || !canAct || autoRan.current || register.isPending) return;
    autoRan.current = true;
    void runReturn({ auto: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, canAct, register.isPending]);

  if (!canAct || action !== 'ask') return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissKey(quoteId), '1');
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  return (
    <Card className="border-sky-200 bg-sky-50/60 dark:border-sky-900/50 dark:bg-sky-950/20">
      <CardContent className="flex items-center gap-3 p-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 dark:bg-sky-950/40">
          <Undo2 className="h-5 w-5 text-sky-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Devolver ao estoque?</p>
          <p className="text-xs text-muted-foreground">
            {plural(lines.length, 'produto baixado', 'produtos baixados')} neste orçamento cancelado.
          </p>
        </div>
        <Button3D size="sm" onClick={() => runReturn()} disabled={register.isPending}>
          Devolver
        </Button3D>
        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={handleDismiss}>
          <X className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
